import path from 'path';

import * as ENGINE from '@gnsx/genesys.js';

import { AssetType } from './assetTypes';
import { extractGlbJson } from './gltfPathReferences';

import { MESH_COMBINATION_FILE_EXT } from '@/const';
import { assetsStore, editorStore } from '@/editor/store';
import { type IAssetFile } from '@/editor/store/models';

/** Types of asset references that can exist */
export const ReferenceType = {
  Prefab: 'prefab',
  Material: 'material',
  Model: 'model',
  Texture: 'texture',
  Hdri: 'hdri',
  Video: 'video',
  Audio: 'audio',
  Scene: 'scene',
  SourceCode: 'sourcecode',
  JsClass: 'jsclass',
  Resource: 'resource',
  Json: 'json',
} as const;

export type ReferenceType = typeof ReferenceType[keyof typeof ReferenceType];

/** A single reference to an asset from another asset */
export interface AssetReference {
  sourceAssetPath: string;      // The asset that contains the reference
  sourceAssetType: AssetType;
  referencePath: string;        // The referenced asset path
  referenceType: ReferenceType;
  location: string;             // JSON path or description of where the reference is
}

/** Result of analyzing dependencies for an asset */
export interface DependencyAnalysisResult {
  assetPath: string;
  assetType: AssetType;
  directReferences: AssetReference[];    // Assets that directly reference this asset
  affectedCount: number;
  hasReferences: boolean;
}

/** Result of analyzing dependencies for every leaf inside a folder. */
export interface FolderDependencyAnalysisResult {
  folderPath: string;
  /** References from outside the folder, pointing into items inside the folder. */
  externalReferences: AssetReference[];
  /** Number of items inside the folder that have at least one external reference. */
  affectedItemCount: number;
  hasReferences: boolean;
}

const DEBUG = {
  logAnalysis: true,
  logScanning: false,
  logReferences: true,
} as const;

const REFERENCE_SOURCE_ASSET_TYPES: readonly AssetType[] = [
  AssetType.Scene,
  AssetType.Prefab,
  AssetType.Material,
  AssetType.Model,
  AssetType.Resource,
  AssetType.VFX,
  AssetType.AnimationConfig,
  AssetType.SkeletonProfile,
  AssetType.MeshCombination,
  AssetType.Json,
] as const;

const JSON_REFERENCE_SCAN_EXTENSIONS = [
  '.json',
  '.gltf',
  '.prefab',
  '.scene',
  ENGINE.SCENE_NAME_EXT.toLowerCase(),
  ENGINE.PREFAB_EXTENSION.toLowerCase(),
  ENGINE.MATERIAL_EXTENSION.toLowerCase(),
  ENGINE.VFX_EXTENSION.toLowerCase(),
  MESH_COMBINATION_FILE_EXT.toLowerCase(),
] as const;
const GLB_REFERENCE_SCAN_EXTENSION = '.glb';

function isJsonScannableAssetPath(sourceAssetPath: string): boolean {
  const normalizedPath = sourceAssetPath.trim().toLowerCase();
  return JSON_REFERENCE_SCAN_EXTENSIONS.some(extension => normalizedPath.endsWith(extension));
}

function isGlbScannableAssetPath(sourceAssetPath: string): boolean {
  return sourceAssetPath.trim().toLowerCase().endsWith(GLB_REFERENCE_SCAN_EXTENSION);
}

function resolveReferenceType(sourceAssetType: AssetType, location: string): ReferenceType {
  if (location.endsWith('prefabName')) {
    return ReferenceType.Prefab;
  }
  if (sourceAssetType === AssetType.Material) {
    return ReferenceType.Texture;
  }
  if (sourceAssetType === AssetType.Resource) {
    return ReferenceType.Resource;
  }
  return ReferenceType.Model;
}

/** Internal scan target — an asset path and (for class files) the class name to search for. */
interface ScanTarget {
  path: string;
  className?: string;
}

/** Returns the class name a SourceCode or JsClass asset declares, derived from its file basename. */
export function getClassNameForAsset(assetPath: string, assetType: AssetType): string | undefined {
  if (assetType !== AssetType.SourceCode && assetType !== AssetType.JsClass) {
    return undefined;
  }
  const base = path.basename(assetPath);
  const ext = path.extname(base);
  const name = ext ? base.slice(0, -ext.length) : base;
  return name.length > 0 ? name : undefined;
}

function scanDataForTargets(
  sourceAssetPath: string,
  sourceAssetType: AssetType,
  data: unknown,
  targets: readonly ScanTarget[],
): AssetReference[] {
  const refs: AssetReference[] = [];
  for (const target of targets) {
    const pathMatches = ENGINE.WorldSerializer.scanAssetPathReferences(data, target.path);
    for (const match of pathMatches) {
      refs.push({
        sourceAssetPath,
        sourceAssetType,
        referencePath: match.value,
        referenceType: resolveReferenceType(sourceAssetType, match.jsonPath),
        location: match.jsonPath,
      });
    }
    if (target.className) {
      const classMatches = ENGINE.WorldSerializer.scanClassNameReferences(data, target.className);
      for (const match of classMatches) {
        refs.push({
          sourceAssetPath,
          sourceAssetType,
          referencePath: match.value,
          referenceType: ReferenceType.JsClass,
          location: match.jsonPath,
        });
      }
    }
  }
  return refs;
}

async function loadSourceAssetData(sourceAssetPath: string): Promise<unknown | null> {
  const storageProvider = editorStore.project.makeScopedStorageProvider();
  try {
    if (isGlbScannableAssetPath(sourceAssetPath)) {
      const file = await storageProvider.downloadFile(ENGINE.AssetPath.fromString(sourceAssetPath));
      const arrayBuffer = file instanceof Blob ? await file.arrayBuffer() : (file as ArrayBuffer);
      return extractGlbJson(arrayBuffer);
    }
    if (isJsonScannableAssetPath(sourceAssetPath)) {
      return await storageProvider.downloadFileAsJson<unknown>(
        ENGINE.AssetPath.fromString(sourceAssetPath)
      );
    }
    return null;
  } catch (error) {
    console.warn(`Failed to load source asset ${sourceAssetPath}:`, error);
    return null;
  }
}

function dedupeReferences(references: AssetReference[]): AssetReference[] {
  return Array.from(new Map(
    references.map(ref => [`${ref.sourceAssetPath}|${ref.location}|${ref.referencePath}`, ref])
  ).values());
}

/**
 * Find references to a set of target assets in a single pass over project source assets.
 * Each source asset is loaded once and scanned against every target.
 */
async function findReferencesForTargets(targets: readonly ScanTarget[]): Promise<AssetReference[]> {
  if (targets.length === 0) {
    return [];
  }

  const allReferences: AssetReference[] = [];

  for (const sourceAssetType of REFERENCE_SOURCE_ASSET_TYPES) {
    const sourceAssets = assetsStore.assetTree
      .getAssetFiles(sourceAssetType)
      .filter(asset => asset.isProjectItem);
    for (const sourceAsset of sourceAssets) {
      const data = await loadSourceAssetData(sourceAsset.path);
      if (!data) continue;
      const refs = scanDataForTargets(sourceAsset.path, sourceAssetType, data, targets);
      if (DEBUG.logScanning && refs.length > 0) {
        console.log(`📄 Found ${refs.length} references in ${sourceAsset.path}`);
      }
      allReferences.push(...refs);
    }
  }

  return dedupeReferences(allReferences);
}

/** Find all assets that reference the target asset (by path, and by class name for class files). */
export async function findAssetReferences(targetAssetPath: string): Promise<AssetReference[]> {
  const normalizedPath = targetAssetPath.replace(/\\/g, '/');
  if (DEBUG.logScanning) {
    console.log(`🔍 Scanning for references to: ${normalizedPath}`);
  }

  const target: ScanTarget = { path: normalizedPath };
  const targetAsset = assetsStore.assetTree.files.get(normalizedPath);
  if (targetAsset) {
    target.className = getClassNameForAsset(normalizedPath, targetAsset.assetType);
  }

  const references = await findReferencesForTargets([target]);

  if (DEBUG.logReferences) {
    console.log(`✅ Found ${references.length} total references to ${normalizedPath}.`);
  }

  return references;
}

/** Format a reference as copy-friendly text */
export function formatAssetReferenceLink(reference: AssetReference): string {
  return `${reference.sourceAssetPath}#${reference.location}`;
}

/** Format multiple references as a newline-delimited copy payload */
export function formatAssetReferencesForCopy(references: AssetReference[]): string {
  return references.map(formatAssetReferenceLink).join('\n');
}

/** Analyze all dependencies for an asset */
export async function analyzeDependencies(assetPath: string): Promise<DependencyAnalysisResult> {
  const normalizedPath = assetPath.replace(/\\/g, '/');
  const asset = assetsStore.assetTree.files.get(normalizedPath);

  if (!asset) {
    throw new Error(`Asset not found: ${normalizedPath}`);
  }

  if (DEBUG.logAnalysis) {
    console.log(`📊 Analyzing dependencies for: ${normalizedPath}`);
  }

  const directReferences = await findAssetReferences(normalizedPath);

  const result: DependencyAnalysisResult = {
    assetPath: normalizedPath,
    assetType: asset.assetType,
    directReferences,
    affectedCount: directReferences.length,
    hasReferences: directReferences.length > 0,
  };

  if (DEBUG.logAnalysis) {
    console.log(`📊 Analysis complete: ${result.affectedCount} assets affected.`);
  }

  return result;
}

function isPathInsideFolder(itemPath: string, folderPath: string): boolean {
  const normalizedItem = itemPath.replace(/\\/g, '/');
  const normalizedFolder = folderPath.replace(/\\/g, '/');
  const prefix = normalizedFolder.endsWith('/') ? normalizedFolder : `${normalizedFolder}/`;
  return normalizedItem === normalizedFolder || normalizedItem.startsWith(prefix);
}

/**
 * Analyze every leaf inside a folder and report references that come from *outside* the folder.
 * Source assets are loaded once and scanned against every leaf target in a single pass.
 */
export async function analyzeFolderDependencies(folderPath: string): Promise<FolderDependencyAnalysisResult> {
  const normalizedFolderPath = folderPath.replace(/\\/g, '/');
  if (DEBUG.logAnalysis) {
    console.log(`📊 Analyzing folder dependencies for: ${normalizedFolderPath}`);
  }

  const leafItems: IAssetFile[] = assetsStore.assetTree
    .getItemsIn(normalizedFolderPath, true)
    .filter(item => !item.isFolder);

  if (DEBUG.logAnalysis && leafItems.length === 0) {
    console.warn(`⚠️ No leaf items found in folder: ${normalizedFolderPath}`);
  }

  const targets: ScanTarget[] = leafItems.map(item => ({
    path: item.path,
    className: getClassNameForAsset(item.path, item.assetType),
  }));

  const allReferences = await findReferencesForTargets(targets);
  const externalReferences = allReferences.filter(
    ref => !isPathInsideFolder(ref.sourceAssetPath, folderPath)
  );

  const affectedItemPaths = new Set<string>();
  for (const ref of externalReferences) {
    // Attempt to find which target this reference belongs to
    const matchedTarget = targets.find(t =>
      t.path === ref.referencePath ||
      (t.className && t.className === ref.referencePath) ||
      (ref.referencePath.startsWith('@project/') && t.path === ref.referencePath) ||
      (!ref.referencePath.startsWith('@project/') && t.path === `@project/${ref.referencePath}`)
    );

    if (matchedTarget) {
      affectedItemPaths.add(matchedTarget.path);
    } else {
      // Fallback: group by source asset and location if we can't map back to a leaf item path precisely
      affectedItemPaths.add(`${ref.sourceAssetPath}|${ref.location}`);
    }
  }

  const result: FolderDependencyAnalysisResult = {
    folderPath,
    externalReferences,
    affectedItemCount: affectedItemPaths.size,
    hasReferences: externalReferences.length > 0,
  };

  if (DEBUG.logAnalysis) {
    console.log(`📊 Folder analysis complete: ${externalReferences.length} external references. Affected items: ${affectedItemPaths.size}`);
  }

  return result;
}

/** Group references by source asset type for UI display */
export function groupReferencesByType(references: AssetReference[]): Map<AssetType, AssetReference[]> {
  const grouped = new Map<AssetType, AssetReference[]>();

  for (const ref of references) {
    const existing = grouped.get(ref.sourceAssetType) ?? [];
    existing.push(ref);
    grouped.set(ref.sourceAssetType, existing);
  }

  return grouped;
}

/** Group references by source asset path for UI display */
export function groupReferencesBySourceAsset(references: AssetReference[]): Map<string, AssetReference[]> {
  const grouped = new Map<string, AssetReference[]>();

  for (const ref of references) {
    const existing = grouped.get(ref.sourceAssetPath) ?? [];
    existing.push(ref);
    grouped.set(ref.sourceAssetPath, existing);
  }

  return grouped;
}

/** Get unique source asset paths from references */
export function getUniqueSourceAssets(references: AssetReference[]): string[] {
  const uniquePaths = new Set<string>();
  references.forEach(ref => uniquePaths.add(ref.sourceAssetPath));
  return Array.from(uniquePaths);
}

/** A prefab-instance actor reference: a scene has an actor at `actors[N]` whose prefab points at `prefabPath`. */
export interface PrefabInstanceReference {
  scenePath: string;
  /** Key into `$root.actors` — works for both array-form ("0", "1") and object-form ("0", "3", ...). */
  actorKey: string;
  prefabPath: string;
  /** Original jsonPath (for diagnostics / logs). */
  location: string;
}

/**
 * Matches an actor-level prefab reference such as
 * `$root.actors[3].prefabName`, `$root.actors.3.prefabName`,
 * `$root.actors[3].$base.$path`, or `$root.actors.3.$base.$path`.
 */
const PREFAB_INSTANCE_LOCATION_RE =
  /^\$root\.actors(?:\[(\d+)\]|\.(\d+))\.(?:prefabName|\$base\.\$path)$/;

/** Extract prefab-instance actor references (scene + actor index + prefab path) from raw asset references. */
export function getPrefabInstanceReferences(references: readonly AssetReference[]): PrefabInstanceReference[] {
  const instances: PrefabInstanceReference[] = [];
  for (const ref of references) {
    if (ref.sourceAssetType !== AssetType.Scene) continue;
    const match = PREFAB_INSTANCE_LOCATION_RE.exec(ref.location);
    if (!match) continue;
    const actorKey = match[1] ?? match[2];
    if (!actorKey) continue;
    instances.push({
      scenePath: ref.sourceAssetPath,
      actorKey,
      prefabPath: ref.referencePath,
      location: ref.location,
    });
  }
  return instances;
}

